import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, Users, Handshake, ArrowLeft, ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Spinner } from "@/components/public/Primitives";
import { getSocialWorkDetail } from "@/lib/api";

export default function SocialWorkDetail() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["social-work-detail", slug],
    queryFn: () => getSocialWorkDetail(slug),
  });

  if (isLoading) return <div className="pt-32"><Spinner /></div>;
  if (isError || !data?.post)
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="text-gray-500">Post not found.</p>
        <Link to="/social-work" className="text-navy font-bold underline mt-4 inline-block">Back to Social Work</Link>
      </div>
    );

  const { post, related = [] } = data;

  return (
    <div data-testid="social-work-detail-page">
      <Seo
        title={post.seoTitle || `${post.title} | Girish Kumar Naidu`}
        description={post.seoDescription || post.shortDescription}
        keywords={`Girish Kumar Naidu, ${post.category}, social work, ${post.location}`}
        image={post.featuredImage}
        schemas={[
          {
            "@context": "https://schema.org", "@type": "Article",
            headline: post.title, image: post.featuredImage, datePublished: post.date,
            author: { "@type": "Person", name: "Girish Kumar Naidu" },
            description: post.shortDescription,
          },
          breadcrumbSchema([
            { name: "Home", url: window.location.origin },
            { name: "Social Work", url: `${window.location.origin}/social-work` },
            { name: post.title },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 bg-navy overflow-hidden">
        <img src={post.featuredImage} alt={post.title} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/85 to-navy/60" />
        <div className="relative max-w-4xl mx-auto px-5 md:px-8">
          <Link to="/social-work" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-slate-300 hover:text-gold mb-7">
            <ArrowLeft className="h-4 w-4" /> Back to Social Work
          </Link>
          <span className="block w-fit px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-wider bg-gold text-white mb-5">{post.category}</span>
          <h1 className="font-display text-3xl md:text-5xl font-black text-white tracking-tighter leading-[1.02]">{post.title}</h1>
          <div className="flex items-center gap-5 font-mono text-xs text-slate-300 mt-6 flex-wrap">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gold" /> {post.date}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" /> {post.location}</span>
            {post.beneficiaryCount && <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-gold" /> {post.beneficiaryCount}</span>}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">{post.fullDescription}</p>

          {post.partners && (
            <div className="mt-10 flex items-start gap-3 p-6 rounded-2xl bg-paper border border-gray-200">
              <Handshake className="h-6 w-6 text-civic shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-navy">Partners & Organisers</p>
                <p className="text-gray-600 text-sm mt-1">{post.partners}</p>
              </div>
            </div>
          )}

          {/* Gallery */}
          {post.galleryImages?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12">
              {post.galleryImages.map((img, i) => (
                <div key={i} className="rounded-2xl overflow-hidden aspect-square border border-gray-200">
                  <img src={img} alt={`${post.title} ${i + 1}`} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-paper py-16 md:py-24 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-2xl md:text-3xl font-black text-navy mb-8 tracking-tight">Related Work</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/social-work/${p.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift">
                  <img src={p.featuredImage} alt={p.title} className="h-44 w-full object-cover" />
                  <div className="p-5">
                    <h3 className="font-display font-bold text-navy group-hover:text-gold transition-colors line-clamp-2">{p.title}</h3>
                    <span className="inline-flex items-center gap-1 text-sm text-navy mt-2 font-bold">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
